import {useCallback, useEffect, useRef, useState} from 'react'
import StyleSheet from '../utils/helpers/styleSheet'
import RenderImg from './editArea/Img'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../utils/helpers/origo'
import {useAppDispatch, useAppSelector} from '../stores/configureStore'
import {
  addImage,
  moveImage,
  setInteractedImage,
  setInert,
  setZoom,
  setRotation,
  setScale,
  resetEditorStore,
} from '../stores/editor/editor.reducer'
import {clamp} from '../utils/helpers/clamp'
import {Config} from '../constants/Config'
import {ImgUrl} from '../models/editor/imgUrl'
import Toolbox from './toolbox/Toolbox'
import {selectToolboxTabPanel} from '../stores/toolbox/toolbox.reducer'
import services from '../utils/services'
import {ImgCaption} from '../models/editor/imgCaption'
import {SaveGraphicCaptionsRequest} from '../models/dto/saveGraphicCaptions.request'
import {useParams} from 'react-router-dom'
import ImgTools from './editArea/ImgTools'

const styles: StyleSheet = {
  editor: {
    width: '100%',
    height: 500,

    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    userSelect: 'none',
  },
  editorColumn: {
    flexBasis: '50%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  editArea: {
    width: '100%',
    height: '100%',
  },
  toolboxColumn: {
    flexBasis: '50%',
    display: 'flex',
    flexDirection: 'column',
    margin: 36,
  },
}

export default function GraphicsEditor() {
  const dispatch = useAppDispatch()
  const {images, zoom, interactedImage, selectedImageId} = useAppSelector(
    store => store.editor,
  )
  const editorRef = useRef<HTMLDivElement | null>(null)
  const zoomRef = useRef<HTMLDivElement | null>(null)
  let {graphicId} = useParams() as any
  const [id, setId] = useState<number | undefined>(undefined)
  const [largeSide, setLargeSide] = useState(500)
  const [dragging, setDragging] = useState(false)

  const onWheel: React.UIEventHandler<HTMLDivElement> = e => {
    if (editorRef.current) {
      const rect = editorRef.current.getBoundingClientRect()
      const newZoomLevel = clamp(
        //@ts-ignore
        e.deltaY > 0 ? zoom.level - 0.1 : zoom.level + 0.1,
        Config.minZoom,
        Config.maxZoom,
      )
      //@ts-ignore
      const imageX = e.clientX - rect.left
      //@ts-ignore
      const imageY = e.clientY - rect.top
      const width = rect.width
      const height = rect.height
      const relativeX = imageX - width / 2
      const relativeY = imageY - height / 2
      const newTranslateX =
        ((zoom.translate.x - relativeX) / zoom.level) * newZoomLevel + relativeX
      const newTranslateY =
        ((zoom.translate.y - relativeY) / zoom.level) * newZoomLevel + relativeY

      dispatch(
        setZoom({
          level: newZoomLevel,
          translate: {x: newTranslateX, y: newTranslateY},
        }),
      )
    }
  }

  useEffect(() => {
    if (editorRef.current) {
      const editorRefCurrent = editorRef.current
      const cancelWheel = (event: WheelEvent) => event.preventDefault()

      editorRefCurrent.addEventListener('wheel', cancelWheel, {passive: false})
      return () => {
        editorRefCurrent.removeEventListener('wheel', cancelWheel)
      }
    }
  }, [editorRef])

  const tryGetBaseGraphic = useCallback(
    async (id: number) => {
      const graphic = (await services.getGraphic(id)).data
      const backgroundImage: ImgUrl = {
        id: uuidv4(),
        type: 'url',
        url: graphic.image,
        brightness: 1,
        filters: {inverse: false, monochrome: false, sepia: false},
        inert: true,
        locked: true,
        mirroredX: false,
        mirroredY: false,
        position: origo,
        rotation: 0,
        scale: 1,
      }
      dispatch(addImage({image: backgroundImage}))
      setLargeSide(() => Math.max(graphic.width, graphic.height))
      graphic.captions.captions.forEach(caption => {
        const img: ImgCaption = {
          id: uuidv4(),
          type: 'caption',
          bold: caption.bold,
          brightness: caption.brightness,
          caption1: 'Szöveg helye',
          caption2: null,
          color: caption.color,
          curvatureLevel: caption.curvatureLevel,
          filters: caption.filters,
          fontFamily: caption.fontFamily,
          height: caption.height,
          width: 500,
          inert: false,
          locked: false,
          italic: caption.italic,
          letterSpacing1: caption.letterSpacing,
          letterSpacing2: 0,
          mirroredX: caption.mirroredX,
          mirroredY: caption.mirroredY,
          position: caption.position,
          rotation: caption.rotation,
          scale: caption.scale,
          underline: caption.underline,
        }
        dispatch(addImage({image: img}))
      })
    },
    [dispatch],
  )

  const reloadEditor = useCallback(async () => {
    if (id) {
      dispatch(resetEditorStore())
      dispatch(setZoom({level: 0.1, translate: origo}))
      dispatch(selectToolboxTabPanel({tabPanel: 'AddCaption'}))
      tryGetBaseGraphic(id)
    }
  }, [dispatch, tryGetBaseGraphic, id])

  useEffect(() => {
    reloadEditor()
  }, [reloadEditor, id])

  useEffect(() => {
    const id = Number.parseInt(graphicId)
    if (id) {
      setId(() => id)
    } else {
      setId(() => undefined)
    }
  }, [graphicId])

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.button === 1) {
      e.preventDefault()
      setDragging(() => true)
      dispatch(setInert({inert: true}))
    }
  }

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    if (interactedImage) {
      switch (interactedImage.interaction) {
        case 'dragging':
          dispatch(
            moveImage({
              offset: {
                x:
                  e.nativeEvent.movementX /
                  window.devicePixelRatio /
                  zoom.level,
                y:
                  e.nativeEvent.movementY /
                  window.devicePixelRatio /
                  zoom.level,
              },
            }),
          )
          break
        case 'scaling':
          dispatch(
            setScale({
              id: interactedImage.id,
              scale:
                (images.find(image => image.id === interactedImage.id)
                  ?.scale as number) -
                e.nativeEvent.movementY / 80,
            }),
          )
          break
        case 'rotating':
          dispatch(
            setRotation({
              id: interactedImage.id,
              rotation:
                (images.find(image => image.id === interactedImage.id)
                  ?.rotation as number) - e.nativeEvent.movementY,
            }),
          )
          break
        default:
          break
      }
    }
    if (dragging) {
      dispatch(
        setZoom({
          ...zoom,
          translate: {
            x:
              zoom.translate.x +
              e.nativeEvent.movementX / window.devicePixelRatio,
            y:
              zoom.translate.y +
              e.nativeEvent.movementY / window.devicePixelRatio,
          },
        }),
      )
    }
  }

  const onMouseLeave = () => {
    setDragging(() => false)
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }

  const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    setDragging(() => false)
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }

  const saveCaptions = async () => {
    if (id) {
      const captions = images.filter(
        image => image.type === 'caption',
      ) as ImgCaption[]
      const data: SaveGraphicCaptionsRequest = {
        scale: 500 / largeSide,
        captions: captions.map(image => ({
          bold: image.bold,
          brightness: image.brightness,
          caption: 'text',
          color: image.color,
          curvatureLevel: image.curvatureLevel,
          filters: image.filters,
          fontFamily: image.fontFamily,
          height: image.height,
          italic: image.italic,
          letterSpacing: image.letterSpacing1,
          mirroredX: image.mirroredX,
          mirroredY: image.mirroredY,
          position: image.position,
          rotation: image.rotation,
          scale: image.scale,
          underline: image.underline,
        })),
      }
      const response = await services.saveGraphicCaptions(id, data)
      let inputField = window.parent.document.querySelector(
        '.acf-field[data-name="feliratok"] input[type="text"]',
      )
      if (inputField) {
        ;(inputField as HTMLInputElement).value = JSON.stringify(response.data)
      }
    }
  }

  const selectedImage = images.find(i => i.id === selectedImageId)
  const imgToolsPosition = (): StyleSheet => {
    if (selectedImage && editorRef.current) {
      const el = document.getElementById(selectedImage.id)
      if (el) {
        const bcr = el.getBoundingClientRect()
        const bcre = editorRef.current.getBoundingClientRect()
        const left = clamp(bcr.left - bcre.left, 70, bcre.width - 70)
        const top = clamp(bcr.top - bcre.top - 40, 20, bcre.height - 30 - 20)

        return {
          left: `${left}px`,
          top: `${top}px`,
        } as StyleSheet
      }
    }
    return {}
  }

  return (
    <>
      <div className="button-container">
        <button type="button" className="editorButton" onClick={saveCaptions}>
          Mentés
        </button>
        <button
          type="button"
          className="editorButton alt"
          onClick={reloadEditor}>
          Frissítés
        </button>
      </div>
      <div style={styles.editor} className="editor">
        <div
          className="editorColumn"
          style={styles.editorColumn}
          ref={editorRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}>
          <div
            style={{
              ...styles.editArea,
            }}>
            <div style={{height: '100%'}} ref={zoomRef}>
              {images.map(image => (
                <RenderImg img={image} key={`editorImageList_${image.id}`} />
              ))}
            </div>
          </div>
          {selectedImage && (
            <ImgTools position={imgToolsPosition()} img={selectedImage} />
          )}
        </div>
        <div style={styles.toolboxColumn} className="toolboxColumn">
          <Toolbox isGraphics />
        </div>
      </div>
    </>
  )
}
